<template>
  <div class="manage-users-page">
    <h1>Manage users</h1>
    <PaginatedListView
      :fetchPage="fetchPage"
      :getRowKey="(u) => u.userId!"
      :sortOptions="sortOptions"
      searchPlaceholder="Search for a name, address, or user ID"
      :showSearch="true"
      :pageSize="25">
      <template #header>
        <div class="grid-cell">User ID</div>
        <div class="grid-cell">Subject</div>
        <div class="grid-cell"></div>
        <div class="grid-cell">Name</div>
        <div class="grid-cell">Email</div>
        <div class="grid-cell">Location</div>
        <div class="grid-cell">Joined</div>
        <div class="grid-cell"></div>
        <div class="grid-cell">OS</div>
        <div class="grid-cell">Trees</div>
        <div class="grid-cell">Sync</div>
        <div class="grid-cell">Sharing</div>
        <div class="grid-cell">Plan</div>
      </template>
      <template #row="{ item: u }: { item: User }">
        <div class="grid-cell mono dim">
          <router-link :to="u.pageUrl">{{ u.id }}</router-link>
        </div>
        <div class="grid-cell mono dim">{{ u.subject }}</div>
        <div class="grid-cell type">
          <img v-if="hasSupervisors(u)" src="@/assets/icons/beanie_256.png" class="supers"/>
          <img v-else-if="u.invited" src="@/assets/icons/mail_open_gray_60.png" class="invited"/>
        </div>
        <div class="grid-cell name">
          <router-link :to="u.pageUrl">{{ getDisplayName(u) }}</router-link>
        </div>
        <div class="grid-cell">
          {{ u.email }}
        </div>
        <div class="grid-cell">
          {{ u.metadata?.location }}
        </div>
        <div class="grid-cell date">
          {{ toDateText(u.createdDate) }}
        </div>
        <div class="grid-cell dim">
          {{ toAgeText(u.createdDate) }}
        </div>
        <div class="grid-cell devices">
          <img v-for="os in getUsage(u)?.syncOSInfos ?? []" :key="os.id" :src="os.icon" :title="os.name" :alt="os.name" :class="os.id" />
        </div>
        <div class="grid-cell rgs">
          <ul class="list-unstyled">
            <li v-for="{ type: t, count } in getUsage(u)?.workspaceBaseTypes ?? []" :key="t.id">
              <img :src="t.icon" :title="t.product" :alt="t.product" />
              <template v-if="count > 1">{{ count }}</template>
            </li>
          </ul>
        </div>
        <div class="grid-cell sync" :class="{ failed: getUsage(u)?.lastSyncState != SyncState.Completed }">
          {{ toAgeText(getUsage(u)?.lastSyncDate) }}
        </div>
        <div class="grid-cell sharing">
          <span v-if="getUsage(u)?.totalFamilyGroups" class="groups">
            <img src="@/assets/icons/everyone_500.png"/>
            {{ getUsage(u)?.totalFamilyGroups }}
          </span>
          <span v-if="getUsage(u)?.sharingUsers" class="users">
            <img src="@/assets/icons/person_gray_150.png"/>
            {{ getUsage(u)?.sharingUsers }}
          </span>
          <span v-if="getUsage(u)?.sharingInvites" class="invites">
            <img src="@/assets/icons/mail_gray_60.png"/>
            {{ getUsage(u)?.sharingInvites }}
          </span>
        </div>
        <div class="grid-cell plan" :class="getPlan(u)?.baseType">
          <span v-if="getPlan(u)" class="badge badge-xs">{{ getPlan(u)?.displayName }}</span>
        </div>
      </template>
    </PaginatedListView>
  </div>
</template>

<style lang="scss" scoped>
.manage-users-page {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

:deep(.results) {
  grid-template-columns: repeat(2, auto) 60px repeat(11, auto);
}

.grid-cell {
  &:not(.dim) a {
    color: #333;
  }

  a:hover {
    color: var(--bs-primary);
  }
}

.mono {
  font-family: monospace;
}

.dim, .dim a {
  color: #aaa;
}

.type img {
  width: 12px;

  &.invited {
    opacity: 50%;
  }
}

.devices {
  display: flex;
  align-items: center;

  img {
    width: 14px;
    filter: grayscale(1);

    &.windows {
      opacity: 70%;
    }

    &.mac {
      opacity: 30%;
    }
  }
}

.rgs {
  display: flex;
  align-items: center;

  ul {
    display: flex;
    gap: 8px;
    color: #888;

    li {
      display: flex;
      align-items: center;
      gap: 4px;
    }
    
    img {
      width: 14px;
    }
  }
}

.sync.failed {
  color: red;
}

.sharing {
  display: flex;
  gap: 8px;
  color: #888;

  .groups img {
    width: 14px;    
    transform: translateY(-1px);
  }

  .users img {
    width: 13px;
    opacity: 80%;
    transform: translateY(-1px);
  }

  .invites img {
    height: 12px;
    width: 16px;
    object-fit: cover;
    filter: brightness(0) saturate(100%) invert(63%) sepia(76%) saturate(5097%) hue-rotate(184deg) brightness(100%) contrast(94%);
  }
}

.plan {
  .badge {
    margin: 1px 0;
  }

  &.gen .badge {
    background-color: #b8eeb8 !important;
  }

  &.plus .badge {
    background-color: lightgreen !important;
  }
}
</style>

<script lang="ts" setup>
import { ref } from 'vue'
import { useUserStore } from '@/gp/UserStore'
import { useSubscriptionStore } from '@/gp/SubscriptionStore'
import { subscriptionPlans, User, UserSort } from '@/gp/GroupAdminModel'
import { SyncState } from '@/rd/ResearchDataModel'
import { DateTime } from 'luxon'
import { DurationUtil } from '@/util/LuxonUtil'
import { isDefined } from '@/util/TypeScriptUtil'
import PaginatedListView from '@/util/PaginatedListView.vue'

const sortOptions = [
  { value: UserSort.CreatedDate, label: 'Newest' },
  { value: UserSort.Surname, label: 'Last name' },
  { value: UserSort.GivenName, label: 'First name' },
]

const sortRef = ref(UserSort.CreatedDate)

const userStore = useUserStore()
const subscriptionStore = useSubscriptionStore()

async function fetchPage(options: { search?: string; sort: UserSort; pageSize: number; next?: string; }) {
  sortRef.value = options.sort
  return await userStore.searchUsersAsync({
    name: options.search,
    sort: options.sort,
    pageSize: options.pageSize,
    next: options.next
  })
}

function getDisplayName(u: User) {
  return sortRef.value === UserSort.Surname
    ? [u.surname, u.givenName].filter(isDefined).join(', ')
    : [u.givenName, u.surname].filter(isDefined).join(' ')
}

function toDateText(dt?: DateTime) {
  return dt?.toLocal().toFormat('yyyy-MM-dd')
}

function toAgeText(dt?: DateTime) {
  return dt ? DurationUtil.toAge(dt.diffNow().negate()) : ''
}

function getUsage(u: User) {
  return userStore.asyncUsage.get(u.id!)?.data
}


function getPlan(u: User) {
  var subs = subscriptionStore.getSubscriptionListForUser(u.id)
  return subscriptionPlans.find(p => p.id == subs.at(0)?.items.at(0)?.price?.productId)
}

function hasSupervisors(u: User) {
  const keyList = userStore.asyncSupervisorKeys.get(u.id!)?.data
  return keyList && keyList.keys.length > 0
}
</script>
